import {
  acceptCompanyServiceContract,
  acceptTruthDeclaration,
  createCompany,
  createInterestedCompany,
  getCompanyById,
  getCompanyPartners,
  getCompanyHistory,
  getCompanyInvitation,
  updateCompany,
  sendCompanyLogo,
  getPartnerQualifications,
  getRepresentativeQualifications,
} from '@/resources/companies';

const types = {
  COMPANY: 'COMPANY',
  COMPANY_CLEAN: 'COMPANY_CLEAN',
  COMPANY_CREATE: 'COMPANY_CREATE',
  COMPANY_HISTORY: 'COMPANY_HISTORY',
  COMPANY_INVITATION: 'COMPANY_INVITATION',
  COMPANY_INVITATION_CLEAN: 'COMPANY_INVITATION_CLEAN',
  COMPANY_SELECT: 'COMPANY_SELECT',
  COMPANY_STATUS_UPDATE: 'COMPANY_STATUS_UPDATE',
  COMPANY_UPDATE: 'COMPANY_UPDATE',
  COMPANY_PARTNERS: 'COMPANY_PARTNERS',
  ADD_PARTNER: 'ADD_PARTNER',
  UPDATE_PARTNER: 'UPDATE_PARTNER',
  REMOVE_PARTNER: 'REMOVE_PARTNER',
  SET_PARTNER_QUALIFICATIONS: 'SET_PARTNER_QUALIFICATIONS',
  SET_REPRESENTATIVE_QUALIFICATIONS: 'SET_REPRESENTATIVE_QUALIFICATIONS',
};

let latestPartnersCompanyId = null;

const mutations = {
  [types.COMPANY_PARTNERS](state, data) {
    if (Array.isArray(data)) {
      state.partners = data;
    } else if (data && Array.isArray(data.data)) {
      state.partners = data.data;
    } else {
      state.partners = [];
    }
  },
  [types.COMPANY](state, data) {
    if (data && typeof data === 'object') {
      state.company = data;
      state.company.shareCapitalDate = data.shareCapitalDate
        ? new Date(data.shareCapitalDate)
        : null;
      state.company.registryDate = data.registryDate ? new Date(data.registryDate) : null;
      state.company.simpleNationalDate = data.simpleNationalDate
        ? new Date(data.simpleNationalDate)
        : null;
    }
  },
  [types.COMPANY_CLEAN](state) {
    state.company = {};
  },
  [types.COMPANY_CREATE](state, data) {
    if (data && typeof data === 'object') {
      state.company = data;
    }
  },
  [types.COMPANY_HISTORY](state, data) {
    if (data && typeof data === 'object') {
      state.companyHistory = data;
      state.companyHistory.shareCapitalDate = data.shareCapitalDate
        ? new Date(data.shareCapitalDate)
        : new Date();
      state.companyHistory.registryDate = data.registryDate
        ? new Date(data.registryDate)
        : new Date();
      state.companyHistory.simpleNationalDate = data.simpleNationalDate
        ? new Date(data.simpleNationalDate)
        : new Date();
    }
  },
  [types.COMPANY_INVITATION](state, data) {
    if (data && typeof data === 'object') {
      state.invitation = data;
    }
  },
  [types.COMPANY_INVITATION_CLEAN](state) {
    state.invitation = null;
  },
  [types.COMPANY_SELECT](state, data) {
    state.company = data;
  },
  [types.COMPANY_STATUS_UPDATE](state, data) {
    state.company.status = data;
  },
  [types.COMPANY_UPDATE](state, data) {
    if (data && typeof data === 'object') {
      state.company = Object.assign(state.company, data);
      state.company.shareCapitalDate = data.shareCapitalDate
        ? new Date(data.shareCapitalDate)
        : new Date();
      state.company.registryDate = data.registryDate ? new Date(data.registryDate) : new Date();
      state.company.simpleNationalDate = data.simpleNationalDate
        ? new Date(data.simpleNationalDate)
        : new Date();
    }
  },
  [types.ADD_PARTNER](state, partner) {
    state.partners.push(partner);
  },
  [types.UPDATE_PARTNER](state, { index, partner }) {
    if (state.partners[index]) {
      state.partners.splice(index, 1, partner);
    }
  },
  [types.REMOVE_PARTNER](state, index) {
    state.partners.splice(index, 1);
  },
  [types.SET_PARTNER_QUALIFICATIONS](state, data) {
    state.partnerQualifications = data;
  },
  [types.SET_REPRESENTATIVE_QUALIFICATIONS](state, data) {
    state.representativeQualifications = data;
  },
};

const actions = {
  async acceptCompanyServiceContract({ commit }, { companyId }) {
    const response = await acceptCompanyServiceContract(companyId);
    if (response.status !== 201) return Promise.reject(response.data);
    return commit(types.COMPANY, response.data);
  },
  async acceptCompantTruthDeclaration({ commit }, { companyId }) {
    const response = await acceptTruthDeclaration(companyId);
    if (response.status !== 201) return Promise.reject(response.data);
    return commit(types.COMPANY, response.data);
  },
  async createCompany({ commit }, dados) {
    const response = await createCompany(dados);
    if (response.status !== 201) return Promise.reject(response);
    return commit(types.COMPANY_CREATE, response.data);
  },
  async createInterestedCompany(_, data) {
    const response = await createInterestedCompany(data);
    if (response.status !== 201) return Promise.reject(response.data);
    return response.data;
  },
  async fetchCompany({ commit, dispatch }, companyId) {
    const response = await getCompanyById(companyId);
    await dispatch('loginCompany', response.data, { root: true });
    if (response.status !== 200) return Promise.reject(response.data);
    return commit(types.COMPANY, response.data);
  },
  async fetchCompanyPartners({ commit }, companyId) {
    latestPartnersCompanyId = companyId ?? null;

    if (!companyId) {
      return commit(types.COMPANY_PARTNERS, []);
    }

    commit(types.COMPANY_PARTNERS, []);

    const response = await getCompanyPartners(companyId);
    if (response.status !== 200) return Promise.reject(response.data);
    if (latestPartnersCompanyId !== companyId) return undefined;
    return commit(types.COMPANY_PARTNERS, response.data);
  },
  async fetchCompanyHistory({ commit }, companyHistoryId) {
    const response = await getCompanyHistory(companyHistoryId);
    if (response.status !== 200) return Promise.reject(response.data);
    return commit(types.COMPANY_HISTORY, response.data);
  },
  async getCompanyInvitation({ commit }, convite) {
    const response = await getCompanyInvitation(convite);
    if (response.status !== 200) return Promise.reject(response);
    return commit(types.COMPANY_INVITATION, response.data);
  },
  async saveCompany({ commit, state }, data) {
    const companyId = state.company.id;
    const response = await updateCompany(companyId, data);
    if (response.status !== 200) return Promise.reject(response.data);
    return commit(types.COMPANY, response.data);
  },
  async sendCompanyLogo({ commit }, data) {
    const { companyId, dataLogo } = data;
    const response = await sendCompanyLogo(companyId, dataLogo);
    if (response.status !== 201) return Promise.reject(response.data);
    return commit(types.COMPANY, response.data);
  },
  async updateCompany(_, data) {
    const response = await updateCompany(data.companyId, data);
    if (response.status !== 200) return Promise.reject(response.data);
    return Promise.resolve(response.data);
  },
  cleanCompanyInvitation({ commit }) {
    commit(types.COMPANY_INVITATION);
  },
  selectCompany({ commit }, data) {
    commit(types.COMPANY_SELECT, data);
  },
  updateCompanyData({ commit }, data) {
    commit(types.COMPANY_UPDATE, data);
  },
  async fetchPartnerQualifications({ commit }) {
    const response = await getPartnerQualifications();
    if (response.status !== 200) return Promise.reject(response.data);
    return commit(types.SET_PARTNER_QUALIFICATIONS, response.data.data);
  },
  async fetchRepresentativeQualifications({ commit }) {
    const response = await getRepresentativeQualifications();
    if (response.status !== 200) return Promise.reject(response.data);
    return commit(types.SET_REPRESENTATIVE_QUALIFICATIONS, response.data.data);
  },
};

const state = {
  company: {
    id: 0,
  },
  companyHistory: {
    id: 0,
  },
  invitation: null,
  partners: [],
  partnerQualifications: [],
  representativeQualifications: [],
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};

export { types };
